import { FLOAT_HP, HULL, PANEL_HP, STOP_H, SUNK_DROP, UPPER_H, VESSEL_HULL, boatLength, bodyLeft, stopX, type Boat } from './boats';
import { INK, drawBarrel, drawFlame, ink, inkLine, rr, shade } from './cartoon';
import type { ParticleSystem } from './trails';

/**
 * The boats, drawn flat and inked like everything else. A raft is a log and planks over a row of
 * pontoon drums, one under each bay, and the drums tell the raft's story: bright and round while
 * the float under them is whole, dull, dented and lower in the water once it is under half, gone
 * to splinters at zero. Vessels (a white yacht, a blue-green trawler, a police launch) are one
 * painted hull each and wear by their body: scorch and cracks first, then smoke off the deck, then
 * fire, and once blown they go under tilted. A second level rides its boat: a plank castle on posts
 * over a raft, a cabin deck on a yacht, with planked ramps at the ends.
 */

export const LOG_TOP = '#cf9354';
export const LOG_BOTTOM = '#b67a42';
const LOG_END = '#e8bb7c';
const PLANK = '#e9b66e';
const TEAK = '#e9d9b8';
const ROPE = '#ecd79c';
const BARREL = '#5a84ad';
const PONTOON = '#3f7cc2';
const POST = '#8a5a2a';
const WHITE_HULL = '#f4f3ee';
const RAIL = '#d8dde2';

export interface BoatScene {
  now: number;
  /** The map's middle: vessels point their bows at it. */
  centerX: number;
  particles: ParticleSystem;
  rng: () => number;
}

/** Every boat, in array order: a second level comes right after the boat it rides, so parents go first. */
export function drawBoats(ctx: CanvasRenderingContext2D, boats: readonly Boat[], scene: BoatScene): void {
  for (const b of boats) {
    if (b.sink > SUNK_DROP - 1) continue;
    const root = (b.parentId && boats.find((x) => x.id === b.parentId)) || b;
    // Everything on one hull bobs together.
    const bob = Math.sin(scene.now / 900 + root.x) * 1.5;
    ctx.save();
    ctx.translate(b.x, b.y + b.sink + bob);
    ctx.rotate(b.angle);
    if (b.parentId) drawUpper(ctx, b, root, scene.now);
    else if (b.kind === 'raft') drawRaft(ctx, b, scene.now);
    else drawVessel(ctx, b, scene);
    ctx.restore();
  }
}

/** Runs of intact panels, as [first, last] indices, so a deck draws as continuous timber between its breaks. */
function runs(b: Boat): [number, number][] {
  const out: [number, number][] = [];
  b.panels.forEach((p, i) => {
    if (!p.alive) return;
    const last = out[out.length - 1];
    if (last && last[1] === i - 1) last[1] = i;
    else out.push([i, i]);
  });
  return out;
}

function hullShadow(ctx: CanvasRenderingContext2D, half: number): void {
  ctx.fillStyle = 'rgba(0, 25, 60, 0.22)';
  ctx.beginPath();
  ctx.ellipse(0, HULL + 8, half + 10, 10, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** The planks the moles stand on, with a seam every `step` px. */
function deckPlanks(ctx: CanvasRenderingContext2D, X0: number, X1: number, step: number, color: string): void {
  rr(ctx, X0 - 3, -6, X1 - X0 + 6, 9, 3);
  ink(ctx, color, 2.5);
  ctx.strokeStyle = 'rgba(70, 35, 10, 0.35)';
  ctx.lineWidth = 1.5;
  for (let px = X0 + step; px < X1 - 4; px += step) {
    ctx.beginPath();
    ctx.moveTo(px, -5);
    ctx.lineTo(px, 2);
    ctx.stroke();
  }
}

/** Splintered stubs and a bobbing bit of log where a panel was. */
function splinters(ctx: CanvasRenderingContext2D, x0: number, w: number, now: number, i: number): void {
  for (const sx of [x0 + 2, x0 + w - 10]) {
    rr(ctx, sx, 26, 8, 14, 2);
    ink(ctx, '#8a5a30', 2);
  }
  const bob = Math.sin(now / 520 + i) * 2;
  rr(ctx, x0 + w / 2 - 12, 6 + bob, 24, 7, 3.5);
  ink(ctx, LOG_TOP, 2);
}

/** Scorch and cracks over a panel `depth` px tall, scaling with how much of its health is gone. */
function cracks(ctx: CanvasRenderingContext2D, x0: number, w: number, hpFrac: number, depth: number, i: number): void {
  if (hpFrac >= 1) return;
  const dmg = 1 - hpFrac;
  ctx.fillStyle = `rgba(20, 10, 4, ${0.45 * dmg})`;
  ctx.fillRect(x0 + 3, 3, w - 6, depth - 6);
  ctx.strokeStyle = INK;
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  const n = 1 + Math.round(dmg * 3);
  for (let c = 0; c < n; c++) {
    const cx = x0 + 10 + ((c * 37 + i * 13) % Math.max(1, w - 20));
    ctx.beginPath();
    ctx.moveTo(cx, 4);
    ctx.lineTo(cx + 5, depth * 0.3);
    ctx.lineTo(cx - 3, depth * 0.55);
    ctx.lineTo(cx + 4, depth * 0.85);
    ctx.stroke();
  }
}

/** Posts every ~34 px and a top rail, `h` above the deck line at `y`. Drawn before the moles, so it reads as the far rail. */
function railing(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, h: number, color: string): void {
  if (x1 - x0 < 12) return;
  const n = Math.max(1, Math.round((x1 - x0) / 34));
  for (let k = 0; k <= n; k++) {
    const px = x0 + ((x1 - x0) * k) / n;
    inkLine(ctx, px, y, px, y - h, 2.5, color, 1.2);
  }
  inkLine(ctx, x0, y - h, x1, y - h, 3, color, 1.2);
}

// ---------- rafts ----------

function drawRaft(ctx: CanvasRenderingContext2D, b: Boat, now: number): void {
  const half = boatLength(b) / 2;
  hullShadow(ctx, half);
  // The drums go in first: the log and the deck sit over their tops.
  b.panels.forEach((p, i) => drawPontoon(ctx, -half + (i + 0.5) * b.panelW, b.panelW, p.float / FLOAT_HP, now, i));
  for (const [i0, i1] of runs(b)) {
    const X0 = -half + i0 * b.panelW;
    const X1 = -half + (i1 + 1) * b.panelW;
    rr(ctx, X0, 2, X1 - X0, 22, 10);
    ink(ctx, LOG_TOP);
    for (const ex of [X0 + 10, X1 - 10]) {
      ctx.beginPath();
      ctx.arc(ex, 13, 8, 0, Math.PI * 2);
      ink(ctx, LOG_END, 2.2);
      ctx.strokeStyle = 'rgba(90, 50, 20, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(ex, 13, 4, 0, Math.PI * 2);
      ctx.stroke();
    }
    deckPlanks(ctx, X0, X1, b.panelW / 2, PLANK);
    // Rope lashings at the joints, down over the drums
    for (let i = i0; i < i1; i++) {
      const jx = -half + (i + 1) * b.panelW;
      inkLine(ctx, jx - 7, -5, jx + 7, HULL - 1, 2.6, ROPE, 1.2);
      inkLine(ctx, jx + 7, -5, jx - 7, HULL - 1, 2.6, ROPE, 1.2);
    }
  }
  b.panels.forEach((p, i) => {
    const x0 = -half + i * b.panelW;
    if (!p.alive) splinters(ctx, x0, b.panelW, now, i);
    else cracks(ctx, x0, b.panelW, p.hp / PANEL_HP, HULL, i);
  });
  // Barrels at the surviving deck ends keep sliding moles aboard
  for (const side of ['left', 'right'] as const) {
    const sx = stopX(b, side);
    if (sx !== null) drawBarrel(ctx, sx - b.x, -4, 18, STOP_H + 2, BARREL);
  }
}

/**
 * The drum under one bay. `f` is the float's health 0..1: bright and round while whole, dark, dented
 * and sitting lower once under half, and at zero nothing is left but splinters.
 */
function drawPontoon(ctx: CanvasRenderingContext2D, cx: number, panelW: number, f: number, now: number, i: number): void {
  const len = panelW - 12;
  if (f <= 0) {
    const bits: [number, number, number, number][] = [[-len * 0.35, 24, 7, 13], [len * 0.08, 27, 5, 10], [len * 0.3, 22, 6, 15]];
    for (const [sx, sy, w, h] of bits) {
      rr(ctx, cx + sx, sy + Math.sin(now / 600 + i) * 1.5, w, h, 2);
      ink(ctx, '#6b4423', 1.8);
    }
    return;
  }
  const hurt = f < 0.5;
  const drop = hurt ? 7 : 0;
  // A barrel on its side: drawBarrel stands one up, so turn the frame a quarter.
  ctx.save();
  ctx.translate(cx - len / 2, 32 + drop);
  ctx.rotate(Math.PI / 2);
  drawBarrel(ctx, 0, 0, 24, len, hurt ? shade(PONTOON, -50) : PONTOON);
  ctx.restore();
  if (hurt) {
    ctx.save();
    ctx.translate(cx, 32 + drop);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
    ctx.beginPath();
    ctx.moveTo(-len * 0.22, -12);
    ctx.lineTo(-len * 0.06, -4);
    ctx.lineTo(len * 0.1, -12);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 1.8;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(len * 0.2, -10);
    ctx.lineTo(len * 0.26, -3);
    ctx.lineTo(len * 0.18, 4);
    ctx.lineTo(len * 0.25, 10);
    ctx.stroke();
    ctx.restore();
  }
}

// ---------- vessels ----------

/** Where a point of a boat's frame is in the world, for particles, which live in world space. */
function toWorld(b: Boat, lx: number, ly: number): [number, number] {
  const c = Math.cos(b.angle);
  const s = Math.sin(b.angle);
  return [b.x + lx * c - ly * s, b.y + b.sink + lx * s + ly * c];
}

function drawVessel(ctx: CanvasRenderingContext2D, b: Boat, scene: BoatScene): void {
  const half = boatLength(b) / 2;
  const bow: 1 | -1 = b.x <= scene.centerX ? 1 : -1;
  const left = bodyLeft(b);
  hullShadow(ctx, half + 16);
  ctx.save();
  ctx.scale(bow, 1);
  if (b.kind === 'yacht') drawYacht(ctx, half);
  else if (b.kind === 'fishing') drawTrawler(ctx, half);
  else drawPolice(ctx, half, scene.now, bow);
  if (left < 0.7) vesselScorch(ctx, half, left, b.id.length);
  ctx.restore();
  // Bollards at the deck ends: the stops that keep a sliding mole aboard.
  for (const side of ['left', 'right'] as const) {
    const sx = stopX(b, side);
    if (sx !== null) bollard(ctx, sx - b.x);
  }
  // Going under: the fire is out once the deck is awash.
  if (b.sink > 120) return;
  const burning = left < 0.25;
  if (left < 0.45 && scene.rng() < (burning ? 0.5 : 0.18)) {
    const [wx, wy] = toWorld(b, (scene.rng() - 0.5) * 2 * (half - 24), -8);
    scene.particles.puff(wx, wy, 0, -0.6, burning ? 9 : 6, scene.rng, burning ? '#4a4d52' : '#7a7f86', 70);
  }
  if (burning) {
    const n = Math.max(3, Math.round(half / 40));
    for (let k = 0; k < n; k++) {
      const fx = -half + 24 + ((half * 2 - 48) * k) / Math.max(1, n - 1);
      const h = 24 + 9 * Math.sin(scene.now / 90 + k * 1.7) + 5 * Math.sin(scene.now / 37 + k * 2.3);
      drawFlame(ctx, fx, -2, h, 5 * Math.sin(scene.now / 130 + k));
    }
  }
}

function bollard(ctx: CanvasRenderingContext2D, x: number): void {
  rr(ctx, x - 5, -STOP_H - 2, 10, STOP_H + 2, 3);
  ink(ctx, '#8a949e', 2);
  rr(ctx, x - 7, -STOP_H - 5, 14, 6, 2);
  ink(ctx, '#6d7681', 2);
}

/** Lamps on a light bar: lit ones glow. */
function lamp(ctx: CanvasRenderingContext2D, x: number, y: number, color: string, lit: boolean): void {
  if (lit) {
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.arc(x, y, 11, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }
  ctx.beginPath();
  ctx.arc(x, y, 4.5, 0, Math.PI * 2);
  ink(ctx, lit ? color : shade(color, -70), 1.5);
}

/** The yacht: a long white wedge with a pointed bow running out past the deck, a blue waterline stripe, portholes and a rail. */
function drawYacht(ctx: CanvasRenderingContext2D, half: number): void {
  ctx.beginPath();
  ctx.moveTo(-half - 4, -2);
  ctx.lineTo(half + 38, -8);
  ctx.quadraticCurveTo(half + 32, 20, half - 8, VESSEL_HULL + 4);
  ctx.lineTo(-half + 8, VESSEL_HULL + 4);
  ctx.quadraticCurveTo(-half - 8, 30, -half - 4, -2);
  ctx.closePath();
  ink(ctx, WHITE_HULL);
  ctx.save();
  ctx.clip();
  ctx.fillStyle = '#2f6fb3';
  ctx.fillRect(-half - 12, 25, half * 2 + 64, 7);
  ctx.fillStyle = '#1b4f8a';
  ctx.fillRect(-half - 12, 32, half * 2 + 64, 3);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fillRect(-half + 6, 2, half * 2 + 30, 3);
  ctx.restore();
  for (let x = -half + 30; x < half - 6; x += 44) {
    ctx.beginPath();
    ctx.arc(x, 15, 5.5, 0, Math.PI * 2);
    ink(ctx, '#8fdcff', 2);
  }
  rr(ctx, -half - 2, -6, half * 2 + 4, 8, 3);
  ink(ctx, TEAK, 2.5);
  railing(ctx, -half + 4, half - 4, -6, 22, RAIL);
}

/** The trawler: a blue-green hull with a raised bow, a wheelhouse aft, a mast with its boom slung back and the net hung off it. */
function drawTrawler(ctx: CanvasRenderingContext2D, half: number): void {
  ctx.beginPath();
  ctx.moveTo(-half - 6, -14);
  ctx.lineTo(half - 10, -14);
  ctx.quadraticCurveTo(half + 20, -16, half + 26, -26);
  ctx.lineTo(half + 22, 24);
  ctx.quadraticCurveTo(half + 2, VESSEL_HULL + 4, half - 30, VESSEL_HULL + 4);
  ctx.lineTo(-half + 6, VESSEL_HULL + 4);
  ctx.quadraticCurveTo(-half - 10, 30, -half - 6, -14);
  ctx.closePath();
  ink(ctx, '#2f8f8a');
  ctx.save();
  ctx.clip();
  ctx.fillStyle = '#1d5f5c';
  ctx.fillRect(-half - 12, -1, half * 2 + 60, 5);
  ctx.fillStyle = '#9a3b2e';
  ctx.fillRect(-half - 12, 31, half * 2 + 60, 12);
  ctx.fillStyle = 'rgba(255,255,255,0.3)';
  ctx.fillRect(-half + 4, -11, half * 2 + 24, 3);
  ctx.restore();
  // The deck the moles stand on, inside the bulwark
  rr(ctx, -half - 2, -3, half * 2 + 4, 6, 2);
  ink(ctx, '#c9b58a', 2);
  // Wheelhouse aft, with its roof and exhaust
  rr(ctx, -half + 10, -58, 70, 46, 4);
  ink(ctx, '#efe6cf');
  for (const wx of [-half + 18, -half + 42]) {
    rr(ctx, wx, -50, 18, 14, 2);
    ink(ctx, '#8fdcff', 2);
  }
  rr(ctx, -half + 5, -63, 80, 7, 2);
  ink(ctx, '#5d6670', 2);
  inkLine(ctx, -half + 70, -63, -half + 70, -82, 5, '#4b525b', 1.5);
  // Mast, boom and net
  const mx = 34;
  inkLine(ctx, mx, -14, mx, -98, 5, POST, 1.5);
  inkLine(ctx, mx, -84, mx - 64, -50, 4, POST, 1.4);
  ctx.beginPath();
  ctx.moveTo(mx - 60, -48);
  ctx.lineTo(mx - 12, -74);
  ctx.lineTo(mx - 4, -40);
  ctx.lineTo(mx - 28, -16);
  ctx.lineTo(mx - 62, -18);
  ctx.closePath();
  ink(ctx, 'rgba(205, 190, 140, 0.6)', 1.5, 'rgba(90, 70, 30, 0.8)');
  ctx.save();
  ctx.clip();
  ctx.strokeStyle = 'rgba(90, 70, 30, 0.55)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let k = -120; k < 60; k += 8) {
    ctx.moveTo(mx + k, -80);
    ctx.lineTo(mx + k + 70, -10);
    ctx.moveTo(mx + k + 70, -80);
    ctx.lineTo(mx + k, -10);
  }
  ctx.stroke();
  ctx.restore();
  inkLine(ctx, mx, -96, mx + 10, -96, 3, '#e8453c', 1.2);
}

/** The police launch: a white hull with a blue stripe and POLICE on the side, a cabin aft and a light bar flashing red and blue. */
function drawPolice(ctx: CanvasRenderingContext2D, half: number, now: number, bow: 1 | -1): void {
  ctx.beginPath();
  ctx.moveTo(-half - 4, -4);
  ctx.lineTo(half + 10, -4);
  ctx.quadraticCurveTo(half + 30, -6, half + 34, 0);
  ctx.lineTo(half + 14, VESSEL_HULL);
  ctx.lineTo(-half + 6, VESSEL_HULL + 2);
  ctx.quadraticCurveTo(-half - 8, 26, -half - 4, -4);
  ctx.closePath();
  ink(ctx, '#f4f6f8');
  ctx.save();
  ctx.clip();
  ctx.fillStyle = '#1f4fa3';
  ctx.fillRect(-half - 12, 9, half * 2 + 60, 10);
  ctx.fillStyle = '#d6362c';
  ctx.fillRect(-half - 12, 20, half * 2 + 60, 2.5);
  ctx.fillStyle = '#2b3a4a';
  ctx.fillRect(-half - 12, 35, half * 2 + 60, 9);
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.fillRect(-half + 4, -1, half * 2 + 30, 3);
  ctx.restore();
  // Lettering is never mirrored: undo the bow flip for the text alone.
  ctx.save();
  ctx.scale(bow, 1);
  ctx.font = "800 13px 'Lilita One', Impact, sans-serif";
  ctx.textAlign = 'center';
  ctx.fillStyle = '#1f4fa3';
  ctx.fillText('POLICE', bow * 36, 33);
  ctx.restore();
  rr(ctx, -half - 2, -6, half * 2 + 4, 8, 3);
  ink(ctx, RAIL, 2.5);
  // Cabin aft with the light bar on its roof
  const cx = -half + 24;
  rr(ctx, cx, -46, 76, 42, 5);
  ink(ctx, '#f4f6f8');
  ctx.fillStyle = '#1f4fa3';
  ctx.fillRect(cx, -16, 76, 6);
  for (const wx of [cx + 8, cx + 40]) {
    rr(ctx, wx, -40, 26, 15, 2.5);
    ink(ctx, '#8fdcff', 2);
  }
  rr(ctx, cx - 4, -51, 84, 7, 2);
  ink(ctx, RAIL, 2);
  rr(ctx, cx + 22, -60, 32, 9, 2.5);
  ink(ctx, '#3a3f47', 2);
  const phase = Math.floor(now / 220) % 2;
  lamp(ctx, cx + 30, -55.5, '#ff3b3b', phase === 0);
  lamp(ctx, cx + 46, -55.5, '#3b8bff', phase === 1);
  railing(ctx, cx + 84, half - 4, -6, 20, RAIL);
}

/** Scorch patches and cracks along the hull, more of them the less body is left under 70%. */
function vesselScorch(ctx: CanvasRenderingContext2D, half: number, left: number, seed: number): void {
  const dmg = (0.7 - left) / 0.7;
  const n = 1 + Math.round(dmg * 4);
  for (let k = 0; k < n; k++) {
    const x = -half + 24 + ((k * 73 + seed * 31) % Math.max(1, half * 2 - 48));
    const y = 12 + (k % 2) * 10;
    ctx.fillStyle = `rgba(20, 10, 4, ${0.35 + 0.3 * dmg})`;
    ctx.beginPath();
    ctx.ellipse(x, y, 18 + dmg * 10, 10 + dmg * 6, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = INK;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x - 6, 0);
    ctx.lineTo(x, 12);
    ctx.lineTo(x - 4, 22);
    ctx.lineTo(x + 5, 34);
    ctx.stroke();
  }
}

// ---------- second levels ----------

/**
 * A raised deck over part of a boat. Its frame sits UPPER_H above the parent's, so the parent deck
 * is at local y = UPPER_H. Flat bays stand on posts (a cabin on a yacht); ramp bays slope down to
 * the deck below on the side their `ramp` says.
 */
function drawUpper(ctx: CanvasRenderingContext2D, b: Boat, root: Boat, now: number): void {
  const half = boatLength(b) / 2;
  const cabin = root.kind === 'yacht';
  const H = UPPER_H;
  b.panels.forEach((p, i) => {
    const X0 = -half + i * b.panelW;
    const X1 = X0 + b.panelW;
    if (!p.alive) {
      splinters(ctx, X0, b.panelW, now, i);
      return;
    }
    if (p.ramp !== 0) {
      const lowX = p.ramp === -1 ? X0 : X1;
      const highX = p.ramp === -1 ? X1 : X0;
      drawRamp(ctx, lowX, H, highX, 0, cabin ? TEAK : PLANK);
      return;
    }
    if (cabin) {
      rr(ctx, X0 - 1, 0, b.panelW + 2, H, 2);
      ink(ctx, WHITE_HULL, 2.5);
      ctx.fillStyle = '#2f6fb3';
      ctx.fillRect(X0 - 1, H - 10, b.panelW + 2, 4);
      rr(ctx, X0 + 10, 16, b.panelW - 20, 22, 4);
      ink(ctx, '#8fdcff', 2);
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillRect(X0 + 13, 19, 6, 16);
    } else {
      for (const px of [X0 + 7, X1 - 7]) inkLine(ctx, px, 2, px, H + 2, 7, POST, 1.6);
      inkLine(ctx, X0 + 7, H - 4, X1 - 7, 8, 3, POST, 1.2);
    }
    deckPlanks(ctx, X0, X1, b.panelW / 2, cabin ? TEAK : PLANK);
    cracks(ctx, X0, b.panelW, p.hp / b.maxHp, cabin ? H : 10, i);
    railing(ctx, X0 + 2, X1 - 2, -6, 24, cabin ? RAIL : POST);
  });
}

/** A planked ramp from the low end at (x0, y0) to the high end at (x1, y1), with a post under its middle. */
function drawRamp(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, color: string): void {
  const mx = (x0 + x1) / 2;
  const my = (y0 + y1) / 2;
  inkLine(ctx, mx, my + 4, mx, Math.max(y0, y1) + 2, 6, POST, 1.4);
  const len = Math.hypot(x1 - x0, y1 - y0);
  ctx.save();
  ctx.translate(x0, y0);
  ctx.rotate(Math.atan2(y1 - y0, x1 - x0));
  rr(ctx, -2, -5, len + 4, 10, 3);
  ink(ctx, color, 2.5);
  ctx.strokeStyle = 'rgba(70, 35, 10, 0.35)';
  ctx.lineWidth = 1.5;
  for (let s = 16; s < len - 6; s += 18) {
    ctx.beginPath();
    ctx.moveTo(s, -4);
    ctx.lineTo(s, 4);
    ctx.stroke();
  }
  ctx.restore();
}
